"use client";

import { useEffect } from "react";
import { isBusy, onIdle } from "@/lib/app-activity";

/**
 * Reload when the bundle running here is older than the deploy serving.
 *
 * Two stale-bundle vectors were measured on the call page: a tab left open
 * across a deploy keeps its JavaScript forever (the login cookie is
 * browser-wide, so the next person's login revives the old tab), and the
 * service worker's cache-first chunks let a stale shell boot fully on a slow
 * network. Both showed as "fixed" calling bugs coming back on one phone.
 *
 * The check asks /build-info for the serving commit on load, whenever the
 * page becomes visible, and every five minutes, and compares it with the
 * commit baked into this bundle. On a mismatch it reloads, but never while a
 * browser call is up (lib/app-activity): then it waits for the call to end.
 * Once per server commit, so a mismatch that a reload cannot cure (an alias
 * pointing elsewhere than the HTML came from) cannot loop.
 */
const BUILD = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? "";
const INTERVAL_MS = 5 * 60 * 1000;
const FIRST_CHECK_MS = 15 * 1000;
const RELOADED_KEY = "xd-reloaded-for-build";

export function VersionCheck() {
  useEffect(() => {
    if (!BUILD) return;
    let stopped = false;
    let unsubscribeIdle: (() => void) | null = null;

    const reloadFor = (sha: string) => {
      try {
        if (sessionStorage.getItem(RELOADED_KEY) === sha) return;
        sessionStorage.setItem(RELOADED_KEY, sha);
      } catch {
        /* no storage: reload anyway, once is the best we can do */
      }
      window.location.reload();
    };

    const check = async () => {
      if (stopped || document.visibilityState !== "visible") return;
      let sha = "";
      try {
        const res = await fetch("/build-info", { cache: "no-store", credentials: "same-origin" });
        if (!res.ok) return;
        sha = String((await res.json())?.sha ?? "");
      } catch {
        return;
      }
      if (!sha || sha === BUILD || stopped) return;
      if (isBusy()) {
        // A call is up: the moment it ends is the moment to reload.
        unsubscribeIdle?.();
        unsubscribeIdle = onIdle(() => {
          unsubscribeIdle?.();
          unsubscribeIdle = null;
          if (!stopped) reloadFor(sha);
        });
        return;
      }
      reloadFor(sha);
    };

    const first = setTimeout(() => void check(), FIRST_CHECK_MS);
    const interval = setInterval(() => void check(), INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearTimeout(first);
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      unsubscribeIdle?.();
    };
  }, []);

  return null;
}
